import React, { useState } from 'react';

import { Progress, ResponseErrorPanel } from '@backstage/core-components';

import VpnKey from '@material-ui/icons/VpnKey';
import Button from '@material-ui/core/Button';
import { Grid, Tooltip } from '@material-ui/core';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { useStorageAccountContext, useStoragePermissions } from '../hooks';
import { useStorageAccount, AccountSAS } from './useStorageAccount';
import { GenerateTokenDialog, CopyTokenDialog } from '../TokenDialog';
import { CardStorageContainers } from '../CardStorageContainers';
import { CardStorageQueue } from '../CardStorageQueue';
import { CardStorageFileshare } from '../CardStorageFileshare';

export const StorageAccount = () => {
  const [openGenerateTokenDialog, setOpenGenerateTokenDialog] = useState(false);
  const [openCopyTokenDialog, setOpenCopyTokenDialog] = useState(false);
  const [sasToken, setSasToken] = useState<AccountSAS>();
  const [tokenDuration, setTokenDuration] = useState('');

  const { data, loading, error, name, namespace } = useStorageAccountContext();
  const { generateSASToken } = useStorageAccount({ name, namespace });
  const alertApi = useApi(alertApiRef);
  const { hasAccountSASPermission } = useStoragePermissions();

  if (loading) return <Progress />;

  if (error) return <ResponseErrorPanel error={error} />;

  const { storagecontainer, storagequeue, storagefileshare } =
    data?.children || {};

  const generateStorageAccountSASToken = async (
    duration: number,
    durationText: string,
  ) => {
    try {
      const sasTokenResponse = await generateSASToken(duration);
      setSasToken(sasTokenResponse);
      setTokenDuration(durationText);

      setOpenCopyTokenDialog(true);
      setOpenGenerateTokenDialog(false);
    } catch (e: any) {
      alertApi.post({
        message: `Error on generate storage account sas token: ${e?.message}`,
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Grid container direction="column" spacing={3}>
        {hasAccountSASPermission && (
          <Grid item>
            <Grid container direction="column" alignContent="flex-end">
              <Grid item>
                <Tooltip title="Generate SAS Token for the storage account">
                  <Button
                    color="primary"
                    startIcon={<VpnKey />}
                    variant="outlined"
                    onClick={() => {
                      setOpenGenerateTokenDialog(true);
                    }}
                  >
                    Generate Token
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        )}

        <GenerateTokenDialog
          name={`storage account ${name}`}
          open={openGenerateTokenDialog}
          handleClick={generateStorageAccountSASToken}
          handleClose={() => setOpenGenerateTokenDialog(false)}
        />

        <CopyTokenDialog
          name={`storage account ${name}`}
          token={sasToken?.sas || ''}
          urls={sasToken?.urls}
          tokenDuration={tokenDuration}
          open={openCopyTokenDialog}
          handleClose={() => setOpenCopyTokenDialog(false)}
        />

        <Grid item>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CardStorageContainers
                data={storagecontainer}
                name={name}
                namespace={namespace}
                stgAccountEntity={data.entity}
              />
            </Grid>

            <Grid item xs={6}>
              <CardStorageQueue
                data={storagequeue}
                name={name}
                namespace={namespace}
              />
            </Grid>

            <Grid item xs={6}>
              <CardStorageFileshare data={storagefileshare} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
